import React from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { i18next, dialogs, view, blocks } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import common from 'src/js/modules/common';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getParentBlockByElement from 'src/js/modules/blocks/helpers/get-parent-block-by-element';
import constants from '../constants';
import updateLiveElementAttribute from '../helpers/update-live-element-attribute';
import getChangedAttributes from '../helpers/get-changed-attributes';

const { Box, Stack, ControlGroup, CheckboxGroup, Slider, CheckboxField, SimpleSelectField } =
  designSystem;
const { useForm } = reactHookForm;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;
const {
  components: { ModalWithPanels },
} = common;
const { setElementAttribute, removeElementAttribute, bulkViewOperations } = view.operations;
const {
  common: { MODAL_WIDTH, DELAY },
  attributes: { AUTO_CHANGE_DELAY },
  optionTypes,
  attributesMap,
} = constants;
const { SLIDER_SETTINGS_DIALOG } = dialogTypes;

const getTransitionOptions = () => [
  {
    value: 'slide',
    label: i18next.t('Slide in'),
  },
  {
    value: 'fade',
    label: i18next.t('Fade in'),
  },
];

const SlideSettingsDialog = ({ elementId, onDialogMainAction, onDialogCancel }) => {
  const element = view.accessors.getLiveElement(elementId);
  const blockElement = getParentBlockByElement(element);
  const blockId = blocks.accessors.getBlockIdByElement(blockElement);
  const transitionOptions = getTransitionOptions();

  const initialValues = {
    [optionTypes.PAGINATION]: element.pagination,
    [optionTypes.NAVIGATION]: element.navigation,
    [optionTypes.TRANSITION]: element.transition,
    [optionTypes.AUTO_CHANGE]: element.autoplay,
    [optionTypes.AUTO_CHANGE_DELAY]: element.autoplayDelay / 1000,
  };

  const {
    control,
    formState: { defaultValues },
    watch,
    getValues,
    setValue,
  } = useForm({
    defaultValues: initialValues,
  });

  const isAutoChanged = watch(optionTypes.AUTO_CHANGE);
  const autoChangeDelayValue = watch(optionTypes.AUTO_CHANGE_DELAY);

  const handleSubmit = () => {
    const values = getValues();
    const { autoChange, autoChangeDelay, transition, navigation, pagination } = values;
    const operations = Object.keys(values).reduce((acc, name) => {
      const value = values[name];
      const attribute = attributesMap[name];

      if (!attribute) {
        return acc;
      }

      return value
        ? [...acc, [setElementAttribute, [elementId, attribute, value]]]
        : [...acc, [removeElementAttribute, [elementId, attribute]]];
    }, []);

    if (autoChange) {
      operations.push([
        setElementAttribute,
        [elementId, AUTO_CHANGE_DELAY, autoChangeDelay * 1000],
      ]);
    } else {
      operations.push([removeElementAttribute, [elementId, AUTO_CHANGE_DELAY]]);
    }

    bulkViewOperations(operations);
    onDialogMainAction({
      elementId,
      autoChange,
      duration: autoChangeDelay,
      navigation,
      pagination,
      transitionType: transition,
    });
    dialogs.operations.hide();
  };

  const handleCancel = () => {
    const changeAttributes = getChangedAttributes(getValues(), defaultValues);

    changeAttributes.forEach(({ name, value }) =>
      updateLiveElementAttribute(elementId, name, value)
    );
    onDialogCancel({ elementId });
    dialogs.operations.hide();
  };

  const handleNavigation = (value) => {
    updateLiveElementAttribute(elementId, attributesMap[optionTypes.NAVIGATION], value);

    trackEvent(events.SLIDER_SETTINGS_OPTION_CLICKED, {
      dialogId: SLIDER_SETTINGS_DIALOG,
      blockId,
      navigation: value,
    });
  };

  const handlePagination = (value) => {
    updateLiveElementAttribute(elementId, attributesMap[optionTypes.PAGINATION], value);

    trackEvent(events.SLIDER_SETTINGS_OPTION_CLICKED, {
      dialogId: SLIDER_SETTINGS_DIALOG,
      blockId,
      pagination: value,
    });
  };

  const handleTransition = ({ value }) => {
    trackEvent(events.SLIDER_SETTINGS_INPUT_CLICKED, {
      dialogId: SLIDER_SETTINGS_DIALOG,
      blockId,
      actionType: value,
    });
  };

  const handleAutoChange = (value) => {
    trackEvent(events.SLIDER_SETTINGS_OPTION_CLICKED, {
      dialogId: SLIDER_SETTINGS_DIALOG,
      blockId,
      autoChange: value,
    });
  };

  const handleAutoChangeDelay = (value) => {
    setValue(optionTypes.AUTO_CHANGE_DELAY, value, { shouldDirty: true });
  };

  return (
    <ModalWithPanels
      width={MODAL_WIDTH}
      height="auto"
      captions={{
        title: i18next.t('Slider settings'),
        submitButton: i18next.t('Submit'),
        cancelButton: i18next.t('Cancel'),
      }}
      elementForPositioning={element}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onOverlayClick={handleSubmit}
      onPressEsc={handleCancel}
    >
      <Box paddingTop="spacing-4-xs" paddingBotttom="spacing-4-xs">
        <Stack gap="spacing-m">
          <ControlGroup
            title={i18next.t('Slider elements')}
            description={i18next.t('Select which elements should be displayed')}
          >
            <CheckboxGroup>
              <CheckboxField
                name={optionTypes.NAVIGATION}
                control={control}
                label={i18next.t('Arrows')}
                onChange={handleNavigation}
              />
              <CheckboxField
                name={optionTypes.PAGINATION}
                control={control}
                label={i18next.t('Pagination dots')}
                onChange={handlePagination}
              />
            </CheckboxGroup>
          </ControlGroup>
          <ControlGroup
            title={i18next.t('Slide transition type')}
            description={i18next.t('Choose the slide transition type for the gallery')}
          >
            <SimpleSelectField
              name={optionTypes.TRANSITION}
              control={control}
              options={transitionOptions}
              onChange={handleTransition}
            />
          </ControlGroup>
          <ControlGroup
            title={i18next.t('Slide options')}
            description={i18next.t(
              'Set your slides to automatically transition at certain intervals'
            )}
          >
            <CheckboxField
              name={optionTypes.AUTO_CHANGE}
              control={control}
              label={i18next.t('Auto change of slides')}
              onChange={handleAutoChange}
            />
          </ControlGroup>
          {isAutoChanged && (
            <ControlGroup
              title={i18next.t('Auto change delay')}
              description={i18next.t('Choose the time between photo transitions')}
              additionalInfo={`${autoChangeDelayValue} ${i18next.t('sec')}`}
            >
              <Slider
                min={DELAY.min}
                max={DELAY.max}
                defaultValue={getValues(optionTypes.AUTO_CHANGE_DELAY)}
                onChange={handleAutoChangeDelay}
              />
            </ControlGroup>
          )}
        </Stack>
      </Box>
    </ModalWithPanels>
  );
};

SlideSettingsDialog.propTypes = {
  elementId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default SlideSettingsDialog;
